import { createSharedComposable } from '@vueuse/core';
import { computed, ref } from 'vue';

import { crossbeamApi } from '@/api';
import { captureException } from '@/errors';
import type { CreditPack, CreditPackPurchaseResponse } from '@/types/billing';

const BEST_VALUE_PRICING_KEY = 'credits-100k@2026-08';

/* Callers branch on the status code: 403 is an entitlement problem, 401 an
 * expired session. 'unknown' is a throw before any response arrived. On
 * 401/403/404/422 nothing was charged; on 409, 5xx or unknown do NOT claim
 * that, since a lost 201 followed by a retry lands as a 409 after the charge. */
export type CreditPackError =
  | { type: 'http'; statusCode: number }
  | { type: 'unknown' };

export type CreditPackPurchaseState =
  | { status: 'idle' }
  | { status: 'submitting' }
  | { status: 'succeeded'; receipt: CreditPackPurchaseResponse }
  | { status: 'failed'; error: CreditPackError };

function bySmallestPack(a: CreditPack, b: CreditPack) {
  return a.credits - b.credits;
}

function useCreditPacks() {
  const packs = ref<CreditPack[]>([]);
  const isLoadingPacks = ref(false);
  const catalogueError = ref<CreditPackError | null>(null);
  const selectedPricingKey = ref<string | null>(null);
  const purchaseState = ref<CreditPackPurchaseState>({ status: 'idle' });

  const selectedPack = computed(
    () =>
      packs.value.find(
        (pack) => pack.pricing_key === selectedPricingKey.value,
      ) ?? null,
  );

  async function loadPacks() {
    if (isLoadingPacks.value) return;
    isLoadingPacks.value = true;
    catalogueError.value = null;
    try {
      const { data, error, response } = await crossbeamApi.GET(
        '/v0.1/credits/pricing/packs',
      );
      const statusCode = response.status;
      if (error || !data) {
        if (statusCode >= 500)
          captureException(
            new Error(`Credit pack catalogue failed with status ${statusCode}`),
          );
        catalogueError.value = { type: 'http', statusCode };
        packs.value = [];
        return;
      }
      if (data.packs.some((pack) => pack.currency !== 'USD'))
        throw new Error('Credit pack catalogue returned a non-USD pack');
      packs.value = [...data.packs].sort(bySmallestPack);
      selectedPricingKey.value ??= packs.value[0]?.pricing_key ?? null;
    } catch (err) {
      captureException(err);
      catalogueError.value = { type: 'unknown' };
      packs.value = [];
    } finally {
      isLoadingPacks.value = false;
    }
  }

  function selectPack(pricingKey: string) {
    selectedPricingKey.value = pricingKey;
  }

  /* expectedCreditLimit is the credit limit the UI was showing. Muncher
   * compares it and the total to what it has stored, so a stale quote or a
   * double submit fails with a 409 instead of charging twice. Never auto-retry
   * a 409. */
  async function purchase(expectedCreditLimit: number) {
    if (purchaseState.value.status === 'submitting') return;
    const pack = selectedPack.value;
    if (!pack) return;

    purchaseState.value = { status: 'submitting' };
    try {
      const { data, error, response } = await crossbeamApi.POST(
        '/v0.1/credits/packs',
        {
          body: {
            pricing_key: pack.pricing_key,
            expected_total_cents: pack.unit_amount_cents,
            expected_credit_limit: expectedCreditLimit,
          },
        },
      );
      const statusCode = response.status;
      if (error || !data) {
        if (statusCode >= 500)
          captureException(
            new Error(`Credit pack purchase failed with status ${statusCode}`),
          );
        purchaseState.value = {
          status: 'failed',
          error: { type: 'http', statusCode },
        };
        return;
      }
      purchaseState.value = { status: 'succeeded', receipt: data };
    } catch (err) {
      captureException(err);
      purchaseState.value = { status: 'failed', error: { type: 'unknown' } };
    }
  }

  function reset() {
    purchaseState.value = { status: 'idle' };
    catalogueError.value = null;
  }

  return {
    packs,
    isLoadingPacks,
    catalogueError,
    selectedPack,
    selectedPricingKey,
    bestValuePricingKey: BEST_VALUE_PRICING_KEY,
    purchaseState,
    loadPacks,
    selectPack,
    purchase,
    reset,
  };
}

export default useCreditPacks;

export const useSharedCreditPacks = createSharedComposable(useCreditPacks);
